import {
  getVariantFromSerializedArray,
  getVariantFromId
} from '@shopify/theme-product';
import '../../styles/components/product-card.scss';

import { getUrlWithVariant } from '@shopify/theme-product-form';
import { multilingual_url } from '../utils/multilingual-url';
import { addToCart } from '../utils/cart';
import { onTablet } from '../utils/breakpoints';

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.productHandle = this.getAttribute('product-handle');
    this.colorSwatchElement = this.querySelector('[data-color-swatches-wrapper]');
    this.colorSwatchSlider = this.querySelector('[data-color-swatches-slider]');
    this.imagesElement = this.querySelector('[data-product-images]');
    this.submitButton = this.querySelector('[data-add-to-cart]');
    this.featuredMedia = this.querySelector('[data-featured-media]');
    this.addToCartButton = this.querySelector('[data-add-to-cart]');
    this.closeAddToCart = this.querySelector('[data-add-to-cart-close-form]');
    this.imageAnchor = this.querySelector('.js-image-link');

    this.mouseOverFunction = this.loadProductCard.bind(this);
    this.refreshColorOverflowFunction = this.refreshColorOverflow.bind(this);
    this.closeFormFunction = this.closeForm.bind(this);
    this.addToCartFunction = this.addToCart.bind(this);

    /**
     * hover interactions only tablet and up
     */
    onTablet(() => {
      this.addEventListener('mouseover', this.mouseOverFunction);
      this.addEventListener('mouseover', this.refreshColorOverflowFunction);
      this.closeAddToCart.addEventListener('click', this.closeFormFunction);
      this.addToCartButton.addEventListener('click', this.addToCartFunction);
    }, () => {
      this.removeEventListener('mouseover', this.mouseOverFunction);
      this.removeEventListener('mouseover', this.refreshColorOverflowFunction);
      this.closeAddToCart.removeEventListener('click', this.closeFormFunction);
      this.addToCartButton.removeEventListener('click', this.addToCartFunction);
    });
  }

  async loadProductCard() {
    this.removeEventListener('mouseover', this.mouseOverFunction);

    const productResponse = await fetch(multilingual_url(`/products/${this.productHandle}.js`));
    this.product = await productResponse.json();

    this.buildColors();

    if (this.hasAttribute('variant')) {
      this.selectVariantId(this.getAttribute('variant'));
    }

    this.attachOptionChangeEvent();
    this.getCurrentOptions();
    this.buildImages(this.hasAttribute('variant'));
    this.updateSoldOutOptions();
    this.updateAddToCartState();
  }

  attachOptionChangeEvent() {
    const options = Array.from(this.querySelectorAll('[data-option-position]'));
    options.forEach(option => {
      option.addEventListener('click', this.selectOption.bind(this));
    });
  }

  selectOption() {
    this.getCurrentOptions();
    this.updateSoldOutOptions();
    this.updateAddToCartState();
    this.buildImages(true);
    this.changeUrl();
  }

  buildColors() {
    const colorOption = this.product.options.find(option => option.name.toLowerCase().includes('color') || option.name.toLowerCase().includes('couleur'));

    if(colorOption) {
      let colorHtml = '';
      colorOption.values.forEach((color, index) => {
        const variant = this.product.variants.find(variant => variant.options[colorOption.position - 1] == color);

        colorHtml += this.colorTemplate({
          color: {
            position: colorOption.position,
            optionName: colorOption.name,
            value: color,
          },
          image: variant.featured_image,
          checked: index === 0
        });
      });

      this.colorSwatchElement.innerHTML = colorHtml;
    }

    if(this.colorSwatchSlider) {
      this.colorSwatchSlider.refreshOverflow();
    }
  }

  colorTemplate({color, image, checked}) {
    return `
     <div class="swiper-slide product-card__swatch-container">
      <label class="product-card__swatch${image ? ' is-color' : ''}">
        <input
          type="radio" data-option-position="${color.position}"
          data-option-name="${color.optionName}"
          value="${color.value}"
          name="${color.optionName}-${this.product.id}${this.hasAttribute('variant') ? `-${this.getAttribute('variant')}` : ''}"
          ${checked ? 'checked' : ''}
        />
        <div class="product-card__swatch-inner">
          <div class="responsive-image__wrapper">
            ${image ? `<img src="${image.src}" alt="${image.alt}">`: window.i18next.buildTextSpan(color.value)}
          </div>
        </div>
      </label>
     </div>
    `;
  }

  refreshColorOverflow() {
    if(this.colorSwatchSlider) {
      this.colorSwatchSlider.refreshOverflow();
    }
  }

  async buildImages(selectVariantImage) {
    const currentColor = this.selectedOptions.find(option => option.name.toLowerCase().includes('color') || option.name.toLowerCase().includes('couleur'));

    if(currentColor) {
      var imagesToShow = this.product.media.filter(image => {
        if(image.media_type === 'image') {
          if(this.currentVariant && this.currentVariant.featured_media && this.currentVariant.featured_media.id == image.id) {
            return false;
          }

          if (image.alt == currentColor.value || !image.alt) {
            return true;
          }
        }
      });
    }
    else {
      var imagesToShow = this.product.media.filter(image => {
        if(image.media_type === 'image') {
          return true;
        }
      });
    }

    if(this.currentVariant) {
      let imagesHtml = ``;
      imagesToShow.forEach(image => {
        imagesHtml += this.imageTemplate({
          id: image.id,
          src: image.src,
          alt: image.alt
        });
      });

      this.imagesElement.innerHTML = imagesHtml;
    }

    if (selectVariantImage && this.currentVariant && this.currentVariant.featured_media) {
      setTimeout(() => {
        const imagesSwiper = this.querySelector('[data-images-swiper]').swiper;

        const imageId = this.currentVariant.featured_media.id;
        const imageElement = this.imagesElement.querySelector(`[data-image-id="${imageId}"]`);
        const imageIndex = [...this.imagesElement.children].indexOf(imageElement);

        imagesSwiper.slideTo(imageIndex)
      });
    }
  }

  imageTemplate({src, alt, id}) {
    return `
      <div class="swiper-slide" data-image-id="${id}">
        <div class="responsive-image__wrapper ratio-rectangle">
          <img src="${src}" alt="${alt ? alt : ''}" loading="lazy" />
        </div>
      </div>
    `;
  }

  closeForm(event) {
    event.currentTarget.closest("[data-options-container]").removeAttribute('open');
  }

  getCurrentOptions() {
    const selectedOptions = Array.from(this.querySelectorAll('[data-option-position]:checked'));

    let selectedOptionsArray = [];
    selectedOptions.forEach(option => {
      const { optionPosition, optionName } = option.dataset;
      const value = option.value;

      /**
       * the order in the array will be the priority for soldout options
       * (first option is never locked)
       */
      if (optionName.toLowerCase().includes('color') || optionName.toLowerCase().includes('couleur')) {
        selectedOptionsArray.unshift({
          position: optionPosition,
          name: optionName,
          value
        });
      }
      else {
        selectedOptionsArray.push({
          position: optionPosition,
          name: optionName,
          value
        });
      }

    });

    this.selectedOptions = selectedOptionsArray;
    this.currentVariant = getVariantFromSerializedArray(this.product, selectedOptionsArray);

    this.changeUrl();
  }

  updateSoldOutOptions() {
    const firstOption = this.selectedOptions[0];
    const secondOption = this.selectedOptions[1];
    const thirdOption = this.selectedOptions[2];

    /**
     * if only one option we check each value
     * and see if there's a variant available with this
     * value. If not we disable the option
     */
    if (this.product.options.length === 1) {
      this.product.options[firstOption.position - 1].values.forEach(value => {
        var availableWithValue = this.product.variants.find(variantCompare =>
          variantCompare.options[firstOption.position - 1] === value &&
          variantCompare.available
        );

        const optionElement = this.querySelector(`[data-option-position="${firstOption.position}"][value="${value}"]`);
        this.disableOptionInput(availableWithValue, optionElement);
      });
    }
    else {
      /**
       * for the second option we check all the values of the second option
       * Then we check if there's an available variant with the first selected option
       * and the current value. If not this value for the second option is diabled.
       */
      this.product.options[secondOption.position - 1].values.forEach(value => {
        var availableWithValue = this.product.variants.find(variantCompare =>
          variantCompare.options[firstOption.position - 1] === firstOption.value &&
          variantCompare.options[secondOption.position - 1] === value &&
          variantCompare.available
        );

        const optionElement = this.querySelector(`[data-option-position="${secondOption.position}"][value="${value}"]`);
        this.disableOptionInput(availableWithValue, optionElement);
      });

      /**
       * if there's a third option, we check all the values of the third option
       * Then we check if there's an available variant with the first selected option AND the second selected option
       * and the current value. If not this value for the third option is diabled.
       */
      if (thirdOption) {
        this.product.options[thirdOption.position - 1].values.forEach(value => {
          var availableWithValue = this.product.variants.find(variantCompare =>
            variantCompare.options[firstOption.position - 1] === firstOption.value &&
            variantCompare.options[secondOption.position - 1] === secondOption.value &&
            variantCompare.options[thirdOption.position - 1] === value &&
            variantCompare.available
          );

          const optionElement = this.querySelector(`[data-option-position="${thirdOption.position}"][value="${value}"]`);
          this.disableOptionInput(availableWithValue, optionElement);
        });
      }
    }
  }

  disableOptionInput(available, optionElement) {
    if (available) {
      optionElement.disabled = false;
    }
    else {
      optionElement.disabled = true;
    }
  }

  updateAddToCartState() {
    if (!this.currentVariant) {
      this.submitButton.disabled = true;
      this.submitButton.innerHTML = theme.strings.product.unavailable;
    } else if (this.currentVariant.available) {
      this.submitButton.disabled = false;
      this.submitButton.innerHTML = theme.strings.product.addToCart;
    } else {
      this.submitButton.disabled = true;
      this.submitButton.innerHTML = theme.strings.product.soldOut;
    }
  }

  selectVariantId(id) {
    const variant = getVariantFromId(this.product, parseInt(id));

    variant.options.forEach((option, index) => {
      const position = index + 1;

      this.querySelector(`[data-option-position='${position}'][value='${option}']`).checked = true;
    })
  }

  changeUrl() {
    const variantUrl = getUrlWithVariant(this.imageAnchor.getAttribute('href'), this.currentVariant.id);
    this.imageAnchor.setAttribute('href', `${variantUrl}`);
  }

  async addToCart() {
    this.addToCartButton.classList.add('loading');

    await addToCart(this.currentVariant.id, 1);

    this.addToCartButton.classList.remove('loading');
  }
}

try {
  customElements.define('product-card', ProductCard);
}
catch(e) {
  console.log("product-card already defined", e)
}
